import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { RedlineAction, RedlineChange } from "work/proposals/redlining/changes/redline-change";
import { FeeScheduleCategoryRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-category-redline";
import FeeScheduleRedlineSchema, { FeeScheduleRedlineJSON } from "./_schema/fee-schedule-redline-schema";
import { FeeScheduleCategoryRedlineJSON } from "work/proposals/redlining/fee-schedule-redline/_schema/fee-schedule-category-redline-schema";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import _ from "lodash";

export class FeeScheduleRedline {
  public static fromJSONArray(arrayObject: FeeScheduleRedlineJSON) {
    const feeScheduleRedline = new FeeScheduleRedline([], []);
    arrayObject.redlines.forEach((redlineCategory: FeeScheduleCategoryRedlineJSON) => {
      const redlineInstance = FeeScheduleCategoryRedline.fromJSON(
        redlineCategory,
        ProposalField.FeeScheduleCategory(
          Guid.fromJSON(
            redlineCategory.originalEntry?.id ?? redlineCategory.revisedEntry?.id ?? redlineCategory.currentEntry?.id,
          ) ?? Guid.generate(),
        ),
      );
      if (redlineInstance) feeScheduleRedline._redlines.push(redlineInstance);
    });
    return feeScheduleRedline;
  }

  public get field(): ProposalField {
    return ProposalField.FeeSchedule;
  }

  public get redlines(): FeeScheduleCategoryRedline[] {
    return this._redlines;
  }

  public get sessionHistory(): RedlineChange[] {
    return [...this._sessionHistory, ...this._redlines.flatMap((redline) => redline.sessionHistory)];
  }

  public get isRevised(): boolean {
    return this._redlines.some((redlineCategory) => redlineCategory.isRevised);
  }

  public get isResolved(): boolean {
    return this._redlines.every((redlineCategory) => redlineCategory.isResolved);
  }

  public get canBeUndone(): boolean {
    return this._redlines.some((redlineCategory) => redlineCategory.canBeUndone);
  }

  public get isEmpty(): boolean {
    return this._redlines.every((redlineCategory) => redlineCategory.currentEntry === null);
  }

  public get hasRejections(): boolean {
    return this._redlines.some((redline) => redline.isRejected);
  }

  public get hasChanges(): boolean {
    return this._redlines.some((redline) => redline.hasChanges);
  }

  /**
   * The fee schedule as it stands once every decision taken so far is applied — the categories a
   * flattened proposal revision carries.
   *
   * Each row contributes its {@link FeeScheduleCategoryRedline.resultingEntry}, so a category still
   * awaiting a decision keeps its place with the requested value rather than disappearing (#4501).
   */
  public get currentEntries(): (FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency)[] {
    return this._redlines
      .map((redline) => redline.resultingEntry)
      .filter((entry): entry is FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency => !!entry);
  }

  constructor(
    originalEntries: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[],
    revisedEntries: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[],
    currentEntries?: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[],
  ) {
    originalEntries.forEach((originalEntry) => {
      const revisedEntry = revisedEntries.find((revisedEntry) => revisedEntry.id?.isEqualTo(originalEntry.id)) ?? null;

      let currentEntry = currentEntries
        ? (currentEntries.find((entry) => entry.id?.isEqualTo(originalEntry.id)) ?? null)
        : undefined;

      if (currentEntry === undefined && revisedEntry?.isEqualTo(originalEntry)) {
        currentEntry = revisedEntry;
      }

      const feeScheduleRedline = new FeeScheduleCategoryRedline(
        originalEntry.id,
        originalEntry,
        revisedEntry,
        currentEntry,
      );
      this._redlines.push(feeScheduleRedline);
    });

    const addedEntriesInRevision = revisedEntries.filter(
      (revisedEntry) => !originalEntries.some((originalEntry) => revisedEntry.id?.isEqualTo(originalEntry.id)),
    );
    addedEntriesInRevision.forEach((revisionAddedEntry) => {
      const currentEntry = currentEntries?.find((currentEntry) => currentEntry.id?.isEqualTo(revisionAddedEntry.id));

      this._redlines.push(
        new FeeScheduleCategoryRedline(revisionAddedEntry.id, null, revisionAddedEntry, currentEntry),
      );
    });

    const addedEntriesInCurrent = currentEntries
      ? currentEntries.filter(
          (currentEntry) =>
            !originalEntries.some((originalEntry) => currentEntry.id?.isEqualTo(originalEntry.id)) &&
            !revisedEntries.some((revisedEntry) => currentEntry.id?.isEqualTo(revisedEntry.id)),
        )
      : [];

    addedEntriesInCurrent.forEach((currentAddedEntry) => {
      this._redlines.push(new FeeScheduleCategoryRedline(currentAddedEntry.id, null, null, currentAddedEntry));
    });
  }

  public clearSessionHistory() {
    this._sessionHistory = [];
    this._redlines.forEach((redline) => redline.clearSessionHistory());
  }

  public addEntry(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): FeeScheduleRedline {
    const newFeeScheduleRedline = this.clone();
    const existingCategoryIndex = newFeeScheduleRedline._redlines.findIndex((redlineCategory) =>
      redlineCategory.field.id?.isEqualTo(category.id),
    );
    if (existingCategoryIndex >= 0) {
      newFeeScheduleRedline._redlines[existingCategoryIndex] =
        newFeeScheduleRedline._redlines[existingCategoryIndex].edit(category);
    } else {
      let newCategoryRedline = new FeeScheduleCategoryRedline(category.id, null, null);
      newCategoryRedline = newCategoryRedline.edit(category);
      newCategoryRedline.clearSessionHistory();

      newFeeScheduleRedline._redlines.push(newCategoryRedline);
      newFeeScheduleRedline._lastEntryUpdated = category.id;

      newFeeScheduleRedline._sessionHistory.push(
        new RedlineChange({
          field: newCategoryRedline.field,
          isResolved: true,
          action: RedlineAction.Add,
          textChanges: newCategoryRedline.textChanges,
        }),
      );
    }
    return newFeeScheduleRedline;
  }

  public getMatchingCategoryRedline(
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ): FeeScheduleCategoryRedline | null {
    if (!category.name) return null;
    return (
      this._redlines.find(
        (redlineCategory) =>
          redlineCategory.field.id?.isEqualTo(category.id) ||
          [
            redlineCategory.originalEntry?.name.toString(),
            redlineCategory.revisedEntry?.name.toString(),
            redlineCategory.currentEntry?.name.toString(),
          ]
            .filter(Boolean)
            .includes(category.name?.toString()),
      ) ?? null
    );
  }

  public replaceEntryById(
    fieldId: Guid,
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ): FeeScheduleRedline {
    const newFeeScheduleRedline = this.clone();
    const targetIndex = newFeeScheduleRedline._redlines.findIndex((existingCategory) =>
      existingCategory.field.id?.isEqualTo(fieldId),
    );
    if (targetIndex === -1) throw new Error("Could not find category to update");
    const targetCategory = newFeeScheduleRedline._redlines[targetIndex];
    newFeeScheduleRedline._redlines[targetIndex] = targetCategory.edit(category);
    newFeeScheduleRedline._lastEntryUpdated = fieldId;
    return newFeeScheduleRedline;
  }

  /**
   * Records a whole-category edit made in the category editor, as the inline cell editors would have.
   *
   * The editor opens on the category as the row showed it and saves the whole category back, so the
   * save alone cannot say which columns the user touched. This compares the saved category with the
   * one the editor opened on, and carries over only the columns that differ. Every other column keeps
   * the row's value as it stands now. That is what stops a save overwriting a column the user never
   * touched, including one the counterparty changed while the editor was open.
   *
   * The result goes through `replaceEntryById`, the same path every inline cell edit takes. The wire
   * diff then sends one `CollectionItemUpdated` for each changed column
   * (`buildCollectionOperations`), so the counterparty sees ordinary column redlines.
   *
   * A save that changes nothing returns the redline unchanged. Passing the unchanged category to
   * `replaceEntryById` is not harmless: on a row still waiting for a decision, it would record the
   * counterparty's value as this party's own and so accept it.
   */
  public reviseEntryById(
    fieldId: Guid,
    openedEntry: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
    savedEntry: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ): FeeScheduleRedline {
    const targetCategory = this._redlines.find((existingCategory) => existingCategory.field.id?.isEqualTo(fieldId));
    if (!targetCategory) throw new Error("Could not find category to update");
    // A removed row's inline editors are read-only, and so is this.
    if (targetCategory.isRemoved) throw new Error("Cannot edit a removed category");
    // The value every inline editor starts from: this party's own entry, else the one asked for.
    const standingEntry = targetCategory.currentEntry ?? targetCategory.revisedEntry;
    if (!standingEntry) throw new Error("Cannot edit a removed category");

    const editedColumns = categoryColumnsEdited(openedEntry, savedEntry);
    if (editedColumns.size === 0) return this.clone();
    return this.replaceEntryById(fieldId, categoryWithEditedColumns(standingEntry, savedEntry, editedColumns));
  }

  public removeEntryByFieldId(categoryId: Guid): FeeScheduleRedline {
    if (!categoryId) throw new Error("No id provided");
    const newFeeScheduleRedline = this.clone();
    const targetIndex = newFeeScheduleRedline._redlines.findIndex((category) =>
      category.field.id?.isEqualTo(categoryId),
    );
    if (targetIndex === -1) throw new Error("Category not found");
    const targetCategoryRedline = newFeeScheduleRedline._redlines[targetIndex];

    newFeeScheduleRedline._sessionHistory = newFeeScheduleRedline._sessionHistory.filter(
      (change) => !change.field?.id?.isEqualTo(categoryId),
    );

    if (targetCategoryRedline.isNewlyAdded && targetCategoryRedline.isResolved) {
      newFeeScheduleRedline._redlines.splice(targetIndex, 1);
    } else {
      newFeeScheduleRedline._redlines[targetIndex] = targetCategoryRedline.remove();
      newFeeScheduleRedline._lastEntryUpdated = categoryId;
    }

    return newFeeScheduleRedline;
  }

  public acceptAll(): FeeScheduleRedline {
    let newRedline = this.clone();
    for (const redline of this._redlines) {
      if (redline.field.id) {
        newRedline = newRedline.acceptRedlineById(redline.field.id);
      }
    }
    return newRedline;
  }

  /**
   * Accepts only the categories still awaiting a decision, leaving every category this party has
   * already resolved — including their own added or edited rates — exactly as it stands. See
   * {@link FieldRedlineArray.acceptUnresolved}.
   */
  public acceptUnresolved(): FeeScheduleRedline {
    let newRedline = this.clone();
    for (const redline of this._redlines) {
      if (redline.field.id && !redline.isResolved) {
        newRedline = newRedline.acceptRedlineById(redline.field.id);
      }
    }
    return newRedline;
  }

  public acceptRedlineById(feeScheduleCategoryId: Guid): FeeScheduleRedline {
    const newFeeScheduleRedline = this.clone();
    const target = newFeeScheduleRedline._redlines.find((redlineFee) =>
      redlineFee.field.id?.isEqualTo(feeScheduleCategoryId),
    );

    if (target) {
      const index = newFeeScheduleRedline._redlines.indexOf(target);
      newFeeScheduleRedline._redlines[index] = target.accept();
      newFeeScheduleRedline._lastEntryUpdated = feeScheduleCategoryId;
    }
    return newFeeScheduleRedline;
  }

  public rejectAll(): FeeScheduleRedline {
    let newRedline = this.clone();
    for (const redline of this._redlines) {
      if (redline.field.id) {
        newRedline = newRedline.rejectRedlineById(redline.field.id);
      }
    }
    return newRedline;
  }

  /**
   * Rejects only the categories still awaiting a decision. See
   * {@link FieldRedlineArray.rejectUnresolved}.
   */
  public rejectUnresolved(): FeeScheduleRedline {
    let newRedline = this.clone();
    for (const redline of this._redlines) {
      if (redline.field.id && !redline.isResolved) {
        newRedline = newRedline.rejectRedlineById(redline.field.id);
      }
    }
    return newRedline;
  }

  public rejectRedlineById(feeScheduleCategoryId: Guid): FeeScheduleRedline {
    const newFeeScheduleRedline = this.clone();
    const target = newFeeScheduleRedline._redlines.find((redlineFee) =>
      redlineFee.field.id?.isEqualTo(feeScheduleCategoryId),
    );

    if (target) {
      const index = newFeeScheduleRedline._redlines.indexOf(target);
      newFeeScheduleRedline._redlines[index] = target.reject();
      newFeeScheduleRedline._lastEntryUpdated = feeScheduleCategoryId;
    }
    return newFeeScheduleRedline;
  }

  public undoAll(): FeeScheduleRedline {
    let newRedline = this.clone();
    for (const redline of this._redlines) {
      if (redline.field.id && redline.canBeUndone) {
        newRedline = newRedline.undoRedlineById(redline.field.id);
      }
    }
    return newRedline;
  }

  public undoRedlineById(feeScheduleCategoryId: Guid): FeeScheduleRedline {
    const newFeeScheduleRedline = this.clone();
    newFeeScheduleRedline._sessionHistory = [];

    const target = newFeeScheduleRedline._redlines.find((redlineCategory) =>
      redlineCategory.field.id?.isEqualTo(feeScheduleCategoryId),
    );

    if (target?.isNewlyAdded && !target.originalEntry) {
      newFeeScheduleRedline._redlines.splice(newFeeScheduleRedline._redlines.indexOf(target), 1);
      const redlineChange = new RedlineChange({
        field: this.field,
        isResolved: true,
        action: RedlineAction.Remove,
        textChanges: target.textChangeCollection.textChanges,
      });
      // Record the removal on the returned redline (not the discarded source), so
      // an undo of a newly-added category carries a session-history action a
      // downstream flatten can recognize as a genuine removal rather than a stale
      // snapshot artifact.
      newFeeScheduleRedline._sessionHistory.push(redlineChange);
    } else if (target) {
      const index = newFeeScheduleRedline._redlines.indexOf(target);
      newFeeScheduleRedline._redlines[index] = target.undo();
    }
    return newFeeScheduleRedline;
  }

  public updateRedline(newFeeScheduleCategoryRedline: FeeScheduleCategoryRedline): FeeScheduleRedline {
    const newFeeScheduleRedline = this.clone();
    const targetCategory = newFeeScheduleRedline._redlines.find((existingCategory) =>
      existingCategory.field.id?.isEqualTo(newFeeScheduleCategoryRedline.field.id),
    );
    if (!targetCategory) {
      console.warn("Could not find category to update");
      return newFeeScheduleRedline;
    }
    const targetIndex = newFeeScheduleRedline._redlines.findIndex((existingCategory) =>
      existingCategory.field.id?.isEqualTo(newFeeScheduleCategoryRedline.field.id),
    );
    newFeeScheduleRedline._redlines[targetIndex] = newFeeScheduleCategoryRedline;
    newFeeScheduleRedline._lastEntryUpdated = newFeeScheduleCategoryRedline.field.id;
    return newFeeScheduleRedline;
  }

  public isEqualTo(other: FeeScheduleRedline): boolean {
    if (this._redlines.length !== other._redlines.length) return false;
    return _.every(this._redlines, (redline) =>
      other._redlines.some((otherRedline) => redline.isEqualTo(otherRedline)),
    );
  }

  public clone(): FeeScheduleRedline {
    const jsonCategories = FeeScheduleRedlineSchema.serialize(this);
    return FeeScheduleRedline.fromJSONArray(jsonCategories);
  }

  public toJSON(): FeeScheduleRedlineJSON {
    return FeeScheduleRedlineSchema.serialize(this);
  }

  protected _lastEntryUpdated: Guid | undefined;
  protected _redlines: FeeScheduleCategoryRedline[] = [];

  private _sessionHistory: RedlineChange[] = [];
}

type FeeScheduleCategoryEntry = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
/** The columns the category editor can change. The fee's rate, currency and unit are one column here. */
type FeeScheduleCategoryColumn = "name" | "description" | "type" | "fee" | "teamMember";

/**
 * The columns the editor's save changed from the category it opened on.
 *
 * Text is compared trimmed, because the editor trims what it saves. Without that, a description that
 * arrived with a trailing space would read as edited and be sent back as a change nobody made.
 */
function categoryColumnsEdited(
  openedEntry: FeeScheduleCategoryEntry,
  savedEntry: FeeScheduleCategoryEntry,
): Set<FeeScheduleCategoryColumn> {
  const edited = new Set<FeeScheduleCategoryColumn>();
  if (!sameText(openedEntry.name, savedEntry.name)) edited.add("name");
  if (!sameText(openedEntry.description, savedEntry.description)) edited.add("description");
  if (openedEntry.type !== savedEntry.type) edited.add("type");
  // A masked fee cannot be read, so the editor cannot change it.
  if (
    openedEntry instanceof FeeScheduleCategory &&
    savedEntry instanceof FeeScheduleCategory &&
    !sameFee(openedEntry.fee, savedEntry.fee)
  ) {
    edited.add("fee");
  }
  if (!sameTeamMember(openedEntry, savedEntry)) edited.add("teamMember");
  return edited;
}

/** The standing category, with the edited columns taken from the saved one. */
function categoryWithEditedColumns(
  standingEntry: FeeScheduleCategoryEntry,
  savedEntry: FeeScheduleCategoryEntry,
  editedColumns: ReadonlySet<FeeScheduleCategoryColumn>,
): FeeScheduleCategoryEntry {
  const pick = <T>(column: FeeScheduleCategoryColumn, standing: T, saved: T): T =>
    editedColumns.has(column) ? saved : standing;
  const params = {
    id: standingEntry.id,
    name: pick("name", standingEntry.name, savedEntry.name),
    description: pick("description", standingEntry.description, savedEntry.description),
    type: pick("type", standingEntry.type, savedEntry.type),
    teamMember: pick("teamMember", standingEntry.teamMember, savedEntry.teamMember),
    teamMemberIsMasked: standingEntry.teamMemberIsMasked,
    version: standingEntry.version,
  };
  if (standingEntry instanceof FeeScheduleCategoryMaskedCurrency) {
    return new FeeScheduleCategoryMaskedCurrency(params);
  }
  const savedFee = savedEntry instanceof FeeScheduleCategory ? savedEntry.fee : standingEntry.fee;
  return new FeeScheduleCategory({ ...params, fee: pick("fee", standingEntry.fee, savedFee) });
}

function sameText(
  first: { value: string } | null | undefined,
  second: { value: string } | null | undefined,
): boolean {
  return (first?.value?.trim() ?? "") === (second?.value?.trim() ?? "");
}

function sameFee(first: FeeScheduleCategory["fee"], second: FeeScheduleCategory["fee"]): boolean {
  if (!first || !second) return !first && !second;
  // A fixed fee's unit arrives as null or undefined depending on where it came from, and the editor
  // saves null. Both mean "no unit", so they must not read as an edited fee.
  return first.rate.isEqualTo(second.rate) && (first.unit || null) === (second.unit || null);
}

function sameTeamMember(first: FeeScheduleCategoryEntry, second: FeeScheduleCategoryEntry): boolean {
  if (!first.teamMember || !second.teamMember) return !first.teamMember && !second.teamMember;
  return first.teamMember.isEqualTo(second.teamMember);
}
